import { GetCartQuery, User } from 'generated/graphql';
import { useSession } from 'next-auth/react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MarketType } from 'constants/markets';
import { UserTypesWithGuestEnum } from '../types/UserTypesWithGuestEnum';

type FavoritesType = number[];

export type UserType = Omit<User, '__typename' | 'user_type'> & {
    user_type: UserTypesWithGuestEnum;
    market: MarketType;
};

type UserStoreType = {
    user?: UserType;
    setUser: (user: UserType) => void;
    favourites?: FavoritesType;
    setFavorites: (favorites: FavoritesType) => void;
    toggleFavourite: (productId: number) => { addedFavourite: boolean };
    cart?: GetCartQuery['cart'] | null;
    setCart: (cart: GetCartQuery['cart']) => void;
    cartId?: number | null;
    setCartId: (cartId?: number | null) => void;
    useLoggedIn: () => boolean;
    useSignedUp: () => boolean;
};

const storeState: Partial<UserStoreType> = {
    user: undefined,
    favourites: [],
    cart: null,
    cartId: null,
};

const useUser = create<UserStoreType>()(
    persist<UserStoreType>(
        ((set, get) => ({
            ...storeState,
            setUser: (user) => set({ user }),
            setFavorites: (favourites) => set({ favourites }),
            toggleFavourite: (productId) => {
                const { addedFavourite, favourites } = toggleFavourite(get().favourites ?? [], productId);
                set({ favourites });

                return { addedFavourite };
            },
            setCartId: (cartId) => set({ cartId }),
            setCart: (cart) => set({ cart }),
            useLoggedIn: () => {
                const session = useSession();

                return session?.status === 'authenticated';
            },
            useSignedUp: () => {
                const session = useSession();

                return session?.data?.role !== 'guest';
            },
        })),
        {
            name: 'customer-cart-data',
            skipHydration: true,
            partialize: (state) => ({
                ...state,
                favourites: undefined,
                cart: undefined,
                user: undefined,
                cartId: state.cartId,
            }),
        },
    ),
);

function toggleFavourite(array: number[], value: number) {
    if (array.includes(value)) {
        return { addedFavourite: false, favourites: array.filter((v) => v !== value) };
    }

    return { addedFavourite: true, favourites: [...array, value] };
}

export default useUser;
